import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useGetIdentity } from "ra-core";
import { ExternalLink, Mail, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { mailHref, waHref } from "../contactLinks";
import { withOptOut } from "../templates/fillTemplate";
import {
  bestChannel,
  GENERIC_PLAY,
  GENERIC_PROPOSAL,
  PLAYBOOK,
  PROPOSAL,
  useBestHour,
  type PlayCo,
} from "../session/playbook";
import { OfferDot } from "../ui";
import { offerColor, useOffers, type Offer } from "../useOffers";
import {
  fecha,
  PendingMark,
  personalizedLabel,
  ResearchButton,
  statusLabel,
  useReadyResearch,
  useResearch,
  useSalesTexts,
  type Cited,
  type Research,
} from "../ai/ai";
import { box, Empty, Label, Quote } from "./FichaTabs";
import {
  revOpener,
  useReviewBrief,
  type ReviewBrief,
} from "../reviews/reviews";
import type { Ficha } from "./useFicha";

/** Productos para la ficha: donde encaja (directorio) o donde ya tiene oportunidad. */
const useFitOffers = (f: Ficha) => {
  const { data: offers } = useOffers();
  const slugs = new Set([
    ...(f.d?.fits ?? []),
    ...f.deals.map((d) => offers?.find((o) => o.id === d.offer_id)?.slug),
  ]);
  return (offers ?? []).filter((o) => slugs.has(o.slug));
};

type T = { text: string; status?: string } | null; // null = descartado por Ian

const fillVars = (s: string, pc: PlayCo) =>
  s
    .replace(/\{\{empresa\}\}/g, pc.nombre)
    .replace(/\{\{municipio\}\}/g, pc.municipio)
    .replace(/\{\{actividad\}\}/g, pc.actividad.toLowerCase())
    .replace(/\{\{rango\}\}/g, pc.rango)
    .replace(/\{\{sitio\}\}/g, pc.sitio ?? "");

/**
 * Textos base por producto: los de la BD (Textos por validar) con su estado; si un
 * producto no tiene textos en la BD, los del código. Los descartados no se muestran.
 */
const useBase = () => {
  const { data: texts } = useSalesTexts();
  return (slug: string, pc: PlayCo) => {
    const P = PROPOSAL[slug] ?? GENERIC_PROPOSAL;
    const G = PLAYBOOK[slug] ?? GENERIC_PLAY;
    const rows = texts?.filter((t) => t.offer_slug === slug) ?? [];
    const t = (kind: string, fallback: string): T => {
      const r = rows.find((x) => x.kind === kind && x.idx === 0);
      if (r?.status === "descartado") return null;
      return {
        text: r ? fillVars(r.body, pc) : fallback,
        status: r?.status ?? "pendiente",
      };
    };
    const objRows = rows.filter((x) => x.kind === "obj");
    return {
      P,
      G,
      open: t(
        slug === "landing" && !pc.sitio ? "open_sin_sitio" : "open",
        P.open(pc),
      ),
      pitch: t("pitch", G.pitch),
      sol: t("sol", P.sol),
      price: t("price", P.price),
      next: t("next", P.next),
      quant: t("quant", P.quant),
      risk: t("risk", P.risk),
      obj: (objRows.length
        ? objRows
            .filter((x) => x.status !== "descartado")
            .map((x) => [x.label ?? "", x.body, x.status] as const)
        : P.obj.map(([a, b]) => [a, b, "pendiente"] as const)
      ).map(([a, b, s]) => [a, b, s] as [string, string, string?]),
    };
  };
};
const Tx = ({ t }: { t: T }) =>
  t ? (
    <>
      {t.text}
      <PendingMark status={t.status} />
    </>
  ) : (
    <span className="text-muted-foreground">—</span>
  );

/** Aviso arriba de Match y Guion: base o personalizada, con el botón para investigar. */
const Banner = ({ f, r }: { f: Ficha; r: Research | null | undefined }) => {
  const { data: current } = useResearch(f.d?.id);
  const st = statusLabel(current);
  if (r)
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>{personalizedLabel(r)}.</span>
        {st && <span className="font-semibold text-foreground">{st}</span>}
      </div>
    );
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed bg-card p-3">
      <div className="min-w-0 flex-1">
        <b>Propuesta base</b>
        <div className="text-sm text-muted-foreground">
          {st ??
            "Hecha solo con los datos del DENUE: se personaliza al investigar con IA (sitio, redes, vacantes, personas clave y noticias)."}
        </div>
      </div>
      {!st && f.d && <ResearchButton ids={[f.d.id]} />}
    </div>
  );
};

/* ------------------------- Match y propuesta ------------------------- */
export const MatchTab = ({
  f,
  onGuion,
}: {
  f: Ficha;
  onGuion: (slug: string) => void;
}) => {
  const list = useFitOffers(f);
  const bestHour = useBestHour();
  const { data: r } = useReadyResearch(f.d?.id);
  const { data: brief } = useReviewBrief(f.d?.id);
  if (!list.length)
    return <Empty>Esta empresa no encaja en ninguna oferta todavía.</Empty>;
  const s = r?.strategy;
  const bySlug = (slug?: string) => list.find((o) => o.slug === slug);
  const lead = bySlug(s?.lider) ?? list[0];
  const after = s?.despues?.length
    ? s.despues.map(bySlug).filter((o): o is Offer => !!o && o.id !== lead.id)
    : list.filter((o) => o.id !== lead.id);
  const [second, ...rest] = after;
  const opener = revOpener(brief, (x) => bySlug(x)?.name ?? x, lead.slug);
  return (
    <div className="flex flex-col gap-3">
      <Banner f={f} r={r} />
      <div className={cn(box, "border-[var(--ai)]")}>
        <h4 className="mb-1 font-bold">
          Estrategia recomendada{r ? " por la IA" : ""}
        </h4>
        <p className="text-sm">
          Entra con <b>{lead.name}</b>
          {r?.match?.[lead.slug]?.pct != null &&
            ` (match ${r.match[lead.slug].pct}%)`}
          {second && (
            <>
              ; cuando llegue a demo, ofrece <b>{second.name}</b> como
              complemento
            </>
          )}
          {!!rest.length && (
            <>
              ; deja {rest.map((o) => o.name).join(", ")} para después del
              primer cierre
            </>
          )}
          .
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
          <li>
            <b>Canal:</b>{" "}
            {s?.canal || bestChannel(f.d?.email_tipo ?? null, f.phone)}.
          </li>
          <li>
            <b>Mejor horario:</b> {s?.horario || bestHour(f.pc.sector)}.
          </li>
          {opener && (
            <li>
              <b>Apertura con sus reseñas:</b> «{opener}»
            </li>
          )}
          <li>
            <b>Plan de 10 días:</b>{" "}
            {s?.plan?.length
              ? s.plan.join(" · ")
              : "día 1 llamada; si no contesta, WhatsApp con la apertura · día 3 correo con un caso de su giro · día 6 llamada en otro horario · día 10 último intento con oferta de cierre."}
          </li>
          {s?.opinion && (
            <li>
              <b>Opinión de la IA:</b> {s.opinion}
              {s.confianza && (
                <>
                  {" "}
                  <b>Confianza {s.confianza}.</b>
                </>
              )}
              {s.por_que_confianza && ` ${s.por_que_confianza}`}
            </li>
          )}
        </ul>
      </div>
      {list.map((o) => (
        <MatchCard
          key={o.id}
          f={f}
          o={o}
          r={r}
          brief={brief}
          onGuion={onGuion}
        />
      ))}
    </div>
  );
};

const MatchCard = ({
  f,
  o,
  r,
  brief,
  onGuion,
}: {
  f: Ficha;
  o: Offer;
  r: Research | null | undefined;
  brief: ReviewBrief | undefined;
  onGuion: (slug: string) => void;
}) => {
  const base = useBase()(o.slug, f.pc);
  const pain = brief?.dolores.find((d) => d.productos.includes(o.slug));
  const m = r?.match?.[o.slug];
  const open = m?.apertura ?? base.open?.text ?? "";
  const pitch = m?.pitch ?? base.pitch?.text ?? "";
  const wa = f.dnc
    ? null
    : waHref(f.phone, withOptOut("whatsapp", `${open} ${pitch}`));
  const mail = f.dnc
    ? null
    : mailHref(
        f.email,
        `${f.name}: una idea para su operación`,
        withOptOut(
          "email",
          `${open}\n\n${m?.solucion ?? base.sol?.text ?? ""}\n\n${m?.siguiente ?? base.next?.text ?? ""}`,
        ),
      );
  const objs = m?.objeciones?.length
    ? m.objeciones.map(([a, b]) => [a, b] as [string, string, string?])
    : base.obj;
  return (
    <div className={cn(box, "flex flex-col gap-3")}>
      <div className="flex flex-wrap items-center gap-2 font-bold">
        <OfferDot color={offerColor(o)} />
        {o.name}
        {m?.pct != null && (
          <span className="ml-auto text-sm font-semibold">Match {m.pct}%</span>
        )}
      </div>
      <dl className="grid gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[160px_1fr]">
        <dt className="text-muted-foreground">Necesidad detectada</dt>
        <dd>{m?.necesidad ?? base.P.need(f.pc)}</dd>
        {m?.evidencia && (
          <>
            <dt className="text-muted-foreground">Evidencia</dt>
            <dd>{m.evidencia}</dd>
          </>
        )}
        {pain && (
          <>
            <dt className="text-muted-foreground">Evidencia en reseñas</dt>
            <dd>
              {pain.menciones} reseñas
              {brief?.incluye_competidores && " de su giro y zona"} mencionan «
              {pain.tema.toLowerCase()}»
              {pain.cita && (
                <span className="text-muted-foreground">
                  {" "}
                  · “{pain.cita.slice(0, 140)}”
                </span>
              )}
            </dd>
          </>
        )}
        <dt className="text-muted-foreground">Cómo la resuelves</dt>
        <dd>{m?.solucion ?? <Tx t={base.sol} />}</dd>
        <dt className="text-muted-foreground">Precio sugerido</dt>
        <dd>
          <Tx t={base.price} />
        </dd>
        <dt className="text-muted-foreground">Siguiente paso</dt>
        <dd>{m?.siguiente ?? <Tx t={base.next} />}</dd>
      </dl>
      <div>
        <Label>Frase de apertura</Label>
        <Quote>{m?.apertura ?? <Tx t={base.open} />}</Quote>
      </div>
      <div>
        <Label>Propuesta en 30 segundos</Label>
        <Quote>{m?.pitch ?? <Tx t={base.pitch} />}</Quote>
      </div>
      {!!objs.length && (
        <div>
          <Label>Objeciones probables</Label>
          <ObjTable rows={objs} />
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        <Button asChild={!!wa} size="sm" variant="outline" disabled={!wa}>
          {wa ? (
            <a href={wa} target="_blank" rel="noreferrer">
              <MessageCircle /> Usar en WhatsApp
            </a>
          ) : (
            <span>
              <MessageCircle /> Usar en WhatsApp
            </span>
          )}
        </Button>
        <Button asChild={!!mail} size="sm" variant="outline" disabled={!mail}>
          {mail ? (
            <a href={mail}>
              <Mail /> Usar en correo
            </a>
          ) : (
            <span>
              <Mail /> Usar en correo
            </span>
          )}
        </Button>
        <Button size="sm" onClick={() => onGuion(o.slug)}>
          Guion completo y estrategia
        </Button>
      </div>
    </div>
  );
};

const ObjTable = ({ rows }: { rows: [string, string, string?][] }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left text-xs text-muted-foreground">
          <th className="py-1.5 pr-3">Si dice…</th>
          <th className="py-1.5">Respóndele…</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([a, b, s]) => (
          <tr key={a} className="border-b last:border-0 align-top">
            <td className="py-1.5 pr-3 font-semibold">{a}</td>
            <td className="py-1.5">
              {b}
              <PendingMark status={s} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* --------------------------- Guion de venta --------------------------- */
export const GuionTab = ({
  f,
  offer,
  setOffer,
}: {
  f: Ficha;
  offer: string | null;
  setOffer: (s: string) => void;
}) => {
  const list = useFitOffers(f);
  const bestHour = useBestHour();
  const baseFor = useBase();
  const { data: r } = useReadyResearch(f.d?.id);
  const { data: brief } = useReviewBrief(f.d?.id);
  const { identity } = useGetIdentity();
  const { data: agenda } = useQuery({
    queryKey: ["crm_settings", "agenda_url"],
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("crm_settings")
        .select("agenda_url")
        .eq("id", 1)
        .maybeSingle();
      return (data?.agenda_url as string | null) ?? null;
    },
  });
  if (!list.length)
    return <Empty>Esta empresa no encaja en ninguna oferta todavía.</Empty>;
  const o = list.find((x) => x.slug === offer) ?? list[0];
  const B = baseFor(o.slug, f.pc);
  const { P, G } = B;
  const A = r?.script?.[o.slug]; // guion personalizado por la IA (si ya se investigó)
  const revOpen = revOpener(
    brief,
    (x) => list.find((y) => y.slug === x)?.name ?? x,
    o.slug,
  );
  const me = identity?.fullName ?? "Ian Aguilar";
  const cal = agenda ?? "[tu liga de agenda]";
  const dec = G.dec.split(" o ")[0];
  const nextText = B.next?.text ?? P.next;
  const nextWord = nextText.split(" ")[0].toLowerCase();
  const ev =
    A?.evidencia ??
    `Trabajo con ${G.proof} de ${f.pc.municipio} y casi todas tenían el mismo problema`;
  const Phase = ({
    n,
    t,
    time,
    goal,
    children,
  }: {
    n: number;
    t: string;
    time: string;
    goal: string;
    children: ReactNode;
  }) => (
    <div className={cn(box, "flex flex-col gap-2")}>
      <div className="flex justify-between gap-2">
        <b>
          {n}. {t}
        </b>
        <span className="text-sm font-semibold text-muted-foreground">
          {time}
        </span>
      </div>
      <span className="text-sm text-muted-foreground">
        <b>Objetivo:</b> {goal}
      </span>
      {children}
    </div>
  );
  const Hint = ({ children }: { children: ReactNode }) => (
    <span className="text-sm text-muted-foreground">{children}</span>
  );
  const day = (d: number, ch: string, txt: string) => (
    <tr key={`${d}${ch}`} className="border-b last:border-0 align-top">
      <td className="py-1.5 pr-3 font-bold whitespace-nowrap">Día {d}</td>
      <td className="py-1.5 pr-3">{ch}</td>
      <td className="py-1.5">{txt}</td>
    </tr>
  );
  const spin: [string, string, string][] = A?.spin?.length
    ? A.spin
    : G.spin.map(([s, q], i) => [s, q, P.listen[i]]);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {list.map((x) => (
          <button
            key={x.id}
            onClick={() => setOffer(x.slug)}
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
              x.id === o.id ? "border-primary bg-accent" : "bg-card",
            )}
          >
            <OfferDot color={offerColor(x)} />
            {x.name}
          </button>
        ))}
      </div>
      <Banner f={f} r={A ? r : null} />
      <div className={cn(box, "border-[var(--ai)]")}>
        <h4 className="mb-2 font-bold">Estrategia de venta · {o.name}</h4>
        <dl className="grid gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[120px_1fr]">
          <dt className="text-muted-foreground">Objetivo único</dt>
          <dd>
            {A?.objetivo ?? (
              <>
                <Tx t={B.next} /> Nada más: no vendas en la primera llamada,
                vende la siguiente reunión.
              </>
            )}
          </dd>
          <dt className="text-muted-foreground">Con quién</dt>
          <dd>
            {A?.con_quien ??
              `${G.dec}. Si contesta alguien más, tu meta es su nombre y su mejor horario.`}
          </dd>
          <dt className="text-muted-foreground">Tesis</dt>
          <dd>
            {A?.tesis ??
              `${f.name} ${P.need(f.pc).split(":")[0].toLowerCase()}; ${o.name} lo resuelve y cuesta menos que el problema.`}
          </dd>
          <dt className="text-muted-foreground">Palancas</dt>
          <dd>
            <b>Evidencia:</b>{" "}
            {(A?.evidencia ?? "lo típico de su giro").replace(/\.$/, "")}.{" "}
            <b>Prueba social:</b>{" "}
            {(A?.prueba_social ?? `${G.proof} de la zona`).replace(/\.$/, "")}.{" "}
            <b>Urgencia:</b>{" "}
            {(A?.urgencia ?? "la competencia ya se está moviendo").replace(
              /\.$/,
              "",
            )}
            . <b>Riesgo cero:</b> <Tx t={B.risk} />
          </dd>
          <dt className="text-muted-foreground">Horario</dt>
          <dd>{r?.strategy?.horario || bestHour(f.pc.sector)}</dd>
        </dl>
      </div>
      <Phase
        n={0}
        t="Antes de marcar"
        time="30 s"
        goal="llegar con contexto, no improvisar."
      >
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {A?.antes?.length ? (
            A.antes.map((x) => <li key={x}>{x}</li>)
          ) : (
            <>
              <li>
                Nombre del decisor (pestaña Contactos) o, si no lo tienes,
                pregunta por “{G.dec}”.
              </li>
              <li>Tu dato de apertura: «{ev}».</li>
              <li>
                Tu número: <Tx t={B.quant} />
              </li>
              <li>
                Dos horarios para proponer la {nextWord}: esta semana y la
                siguiente.
              </li>
            </>
          )}
        </ul>
      </Phase>
      <Phase
        n={1}
        t="Si contesta recepción"
        time="20 s"
        goal="conseguir al decisor o su nombre y horario."
      >
        <Quote>
          {A?.recepcion ??
            `Hola, buen día. Soy ${me}, de Raftel. ¿Me podría comunicar con ${dec}? Es sobre un tema de operación que vimos en su negocio.`}
        </Quote>
        <Hint>
          Si no está: «¿Cómo se llama y a qué hora es mejor encontrarle?».
          Registra ambos y agenda la llamada.
        </Hint>
      </Phase>
      <Phase
        n={2}
        t="Apertura con permiso"
        time="15 s"
        goal="que te dé 30 segundos por voluntad propia."
      >
        <Quote>
          {A?.apertura ??
            `Hola, ¿hablo con ${dec}? Soy ${me}, de Raftel. Le llamo por algo muy puntual. ¿Me regala 30 segundos y usted decide si seguimos?`}
        </Quote>
        <Hint>
          Pedir permiso baja la guardia. Si dice “estoy ocupado”, pregunta: «¿Le
          marco hoy a las 5 o mañana a las 10?»
        </Hint>
      </Phase>
      <Phase
        n={3}
        t="Motivo con evidencia"
        time="20 s"
        goal="que sienta que lo investigaste, no que es una llamada masiva."
      >
        <Quote>
          {A?.motivo ?? (
            <>
              {ev}. <Tx t={B.pitch} /> ¿Le puedo hacer dos preguntas rápidas
              para ver si aplica en su caso?
            </>
          )}
        </Quote>
        {revOpen && (
          <>
            <Label>O ábrela con lo que dicen sus reseñas</Label>
            <Quote>{revOpen}</Quote>
          </>
        )}
        <Hint>Termina siempre en pregunta: pasa la conversación a él.</Hint>
      </Phase>
      <Phase
        n={4}
        t="Descubrimiento"
        time="2 a 4 min"
        goal="que él diga el problema y lo ponga en pesos. Habla 30 %, escucha 70 %."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1.5 pr-3">Paso</th>
                <th className="py-1.5 pr-3">Pregunta</th>
                <th className="py-1.5">Qué escuchar</th>
              </tr>
            </thead>
            <tbody>
              {spin.map(([step, q, l]) => (
                <tr key={step} className="border-b last:border-0 align-top">
                  <td className="py-1.5 pr-3 font-bold">{step}</td>
                  <td className="py-1.5 pr-3">{q}</td>
                  <td className="py-1.5 text-muted-foreground">{l}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Phase>
      <Phase
        n={5}
        t="Propuesta a la medida"
        time="1 min"
        goal="conectar su dolor, en sus palabras, con tu solución."
      >
        <Quote>
          {A?.propuesta ?? (
            <>
              Por lo que me dice, [repite su problema con sus palabras]. Eso es
              justo lo que resolvemos: <Tx t={B.sol} /> <Tx t={B.quant} />
            </>
          )}
        </Quote>
      </Phase>
      <Phase
        n={6}
        t="Objeciones"
        time="según haga falta"
        goal="no discutir: Reconocer, Preguntar, Responder y Confirmar."
      >
        <ObjTable
          rows={[
            ...(A?.objeciones?.length
              ? A.objeciones.map(
                  ([a, b]) => [a, b] as [string, string, string?],
                )
              : B.obj),
            [
              "Mándeme información",
              `Con gusto. Para mandarle lo que sí le sirva: ¿qué le importa más, ${G.spin[1][1].replace(/[¿?]/g, "").toLowerCase()} o el costo? Y agendamos 15 minutos para verlo juntos.`,
            ],
            [
              "Ahorita no es buen momento",
              "Entiendo. ¿Qué tendría que pasar para que sí lo sea? Le busco en esa fecha.",
            ],
            [
              "¿Cuánto cuesta?",
              `Depende de su operación; por eso la ${nextWord}. Para darle idea: ${B.price?.text ?? ""}`,
              B.price?.status,
            ],
          ]}
        />
        <Hint>
          Después de responder, confirma: «¿Eso resuelve su duda?». Si la
          objeción se repite dos veces, regístrala: alimenta el termómetro y el
          Radar.
        </Hint>
      </Phase>
      <Phase
        n={7}
        t="Cierre"
        time="30 s"
        goal="fecha y hora concretas, confirmadas por escrito."
      >
        <Quote>
          {A?.cierre ??
            "¿Le queda mejor el martes a las 10 o el jueves a las 5? Le mando la confirmación por WhatsApp a este número."}
        </Quote>
        <Hint>
          Si prefiere elegir él: «Le mando mi agenda para que escoja: {cal}».
        </Hint>
        <Hint>
          Doble alternativa, nunca «¿cuándo puede?». Al colgar, manda la
          confirmación y registra “Demo agendada”.
        </Hint>
      </Phase>
      <Phase n={8} t="Si no contesta" time="" goal="dejar huella en 3 canales.">
        <div>
          <Label>Mensaje de voz (20 s)</Label>
          <Quote>
            {A?.voz ??
              `Hola, soy ${me}, de Raftel. Le llamo porque ${ev.charAt(0).toLowerCase() + ev.slice(1)}. Le mando un WhatsApp con el detalle.`}
          </Quote>
        </div>
        <div>
          <Label>WhatsApp</Label>
          <Quote>
            {A?.whatsapp ?? (
              <>
                Hola, soy {me.split(" ")[0]} de Raftel. {ev}. <Tx t={B.pitch} />{" "}
                ¿Le parece si lo vemos 15 minutos esta semana? Puede elegir el
                horario aquí: {cal}
              </>
            )}
          </Quote>
        </div>
        <div>
          <Label>Correo</Label>
          <Quote>
            {A?.correo
              ? `Asunto: ${A.correo_asunto ?? `${f.name}: una idea para su operación`}\n\n${A.correo}${/BAJA/.test(A.correo) ? "" : "\n\nSi no quiere recibir más correos, responda BAJA."}`
              : `Asunto: ${f.name}: una idea para su operación\n\n${ev}. ${B.sol?.text ?? ""} ${B.quant?.text ?? ""}\n\n¿Le parece una llamada de 15 minutos el martes o el jueves?\n\n${me} · Raftel\nSi no quiere recibir más correos, responda BAJA.`}
            {!A?.correo && <PendingMark status={B.sol?.status} />}
          </Quote>
        </div>
      </Phase>
      <div className={box}>
        <h4 className="mb-2 font-bold">Cadencia de 14 días</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {day(1, "Llamada", `Guion completo en ${bestHour(f.pc.sector)}`)}
              {day(1, "WhatsApp", "Si no contesta: mensaje de arriba")}
              {day(3, "Correo", "Caso de un cliente de su giro con un número")}
              {day(5, "Llamada", "Otro horario; pide hablar con quien decide")}
              {day(
                8,
                "WhatsApp",
                "Prueba social: “esta semana arrancamos con otra empresa de su zona”",
              )}
              {day(11, "Llamada", "Último intento en vivo")}
              {day(
                14,
                "Correo",
                "Despedida: «¿Lo dejo aquí o lo retomamos en un mes?» (es el que más respuestas trae)",
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Phase
        n={9}
        t="Después de la llamada"
        time="1 min"
        goal="que el CRM aprenda de cada llamada."
      >
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>Resultado, dolor confirmado y siguiente paso con fecha.</li>
          <li>
            Si dijo que no, el motivo: precio, tiempo, ya tiene proveedor o no
            le duele.
          </li>
          <li>
            Todo esto recalibra el termómetro y alimenta el Radar de producto.
          </li>
        </ul>
      </Phase>
    </div>
  );
};

/* ---------------------------- Investigación ---------------------------- */
const Src = ({ c }: { c: Cited }) => {
  const u =
    c.url && /^https?:/.test(c.url)
      ? c.url
      : c.fuente && /^https?:/.test(c.fuente)
        ? c.fuente
        : null;
  let host = "";
  try {
    host = u ? new URL(u).host.replace(/^www\./, "") : "";
  } catch {
    host = "";
  }
  return (
    <span className="text-xs text-muted-foreground">
      {u ? (
        <a
          href={u}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-0.5 hover:underline"
        >
          {host || "fuente"} <ExternalLink className="size-3" />
        </a>
      ) : (
        c.fuente
      )}
      {c.fecha && ` · ${c.fecha.split("-").reverse().join("/")}`}
      {c.sin_confirmar === true && (
        <b className="ml-1 rounded bg-amber-100 px-1 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
          sin confirmar
        </b>
      )}
    </span>
  );
};
const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className={box}>
    <h4 className="mb-2 font-bold">{title}</h4>
    {children}
  </div>
);
const Rows = ({ rows }: { rows: { k: string; v: ReactNode; c: Cited }[] }) => (
  <dl className="grid gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[160px_1fr]">
    {rows.map((r, i) => (
      <div key={i} className="contents">
        <dt className="text-muted-foreground">{r.k}</dt>
        <dd>
          {r.v} <Src c={r.c} />
        </dd>
      </div>
    ))}
  </dl>
);

export const IntelTab = ({ f }: { f: Ficha }) => {
  const { data: current } = useResearch(f.d?.id);
  const { data: r } = useReadyResearch(f.d?.id);
  const st = statusLabel(current);
  const d = r?.dossier;
  if (!r || !d)
    return (
      <div className={cn(box, "flex flex-col gap-2")}>
        <b>{st ?? "Todavía no investigamos esta empresa."}</b>
        <span className="text-sm text-muted-foreground">
          La IA revisa su sitio web (inicio, contacto y nosotros) y tecnología,
          redes, noticias, vacantes, perfiles públicos de LinkedIn (solo como
          enlaces), SIGER, REPSE y la lista 69-B del SAT, y competidores
          cercanos. Con eso arma el expediente y la propuesta de cada producto.
          Cada dato guarda su fuente y fecha; lo que no tenga fuente comprobable
          se marca «sin confirmar».
        </span>
        {(!current ||
          current.status === "error" ||
          current.status === "lista") && (
          <div>
            {f.d ? (
              <ResearchButton
                ids={[f.d.id]}
                label={
                  current?.status === "error"
                    ? "Intentar de nuevo"
                    : "Investigar con IA"
                }
              />
            ) : null}
          </div>
        )}
      </div>
    );
  const secs = r.duration_ms ? Math.round(r.duration_ms / 1000) : null;
  const L = d.lista_69b;
  const hits = L?.coincidencias ?? [];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>
          Investigada con {r.engine} el {fecha(r.finished_at)}
          {secs != null && ` · ${secs} s`} · {r.ai_calls} consulta
          {r.ai_calls === 1 ? "" : "s"} de IA
          {r.search_calls ? ` · ${r.search_calls} búsquedas` : ""} · cada dato
          guarda su fuente y fecha
        </span>
        {st && <b className="text-foreground">{st}</b>}
      </div>
      {!!d.limitaciones?.length && (
        <div className="rounded-lg border border-[var(--warning,#d4a017)] p-2.5 text-sm">
          {d.limitaciones.join(" ")}
        </div>
      )}
      {d.resumen?.texto && (
        <Section title="Resumen del negocio">
          <p className="text-sm">
            {d.resumen.texto} <Src c={d.resumen} />
          </p>
        </Section>
      )}
      {!!d.datos_clave?.length && (
        <Section title="Datos clave">
          <Rows
            rows={d.datos_clave.map((x) => ({ k: x.dato, v: x.valor, c: x }))}
          />
        </Section>
      )}
      {!!(
        d.presencia_digital?.length ||
        d.tecnologia?.length ||
        d.tecnologia_detectada?.length
      ) && (
        <Section title="Presencia digital y tecnología">
          <Rows
            rows={[
              ...(d.presencia_digital ?? []).map((x) => ({
                k: x.canal,
                v: x.url ? (
                  <a
                    href={x.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    {x.valor}
                  </a>
                ) : (
                  x.valor
                ),
                c: { ...x, url: undefined },
              })),
              ...(d.tecnologia ?? []).map((x) => ({
                k: "Tecnología",
                v: x.dato,
                c: x,
              })),
              ...(d.tecnologia_detectada?.length
                ? [
                    {
                      k: "Detectado en su sitio",
                      v: d.tecnologia_detectada.join(", "),
                      c: { fuente: "Código del sitio web" },
                    },
                  ]
                : []),
            ]}
          />
        </Section>
      )}
      {!!d.senales?.length && (
        <Section title="Señales de compra">
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {d.senales.map((x, i) => (
              <li key={i}>
                {x.texto} <Src c={x} />
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title="Socios, directivos y personas clave">
        {d.personas?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-3">Nombre</th>
                  <th className="py-1.5 pr-3">Cargo</th>
                  <th className="py-1.5 pr-3">Contacto de trabajo</th>
                  <th className="py-1.5 pr-3">Perfil público</th>
                  <th className="py-1.5">Fuente</th>
                </tr>
              </thead>
              <tbody>
                {d.personas.map((p, i) => (
                  <tr key={i} className="border-b last:border-0 align-top">
                    <td className="py-1.5 pr-3 font-semibold">{p.nombre}</td>
                    <td className="py-1.5 pr-3">
                      {p.puesto ?? "—"}
                      {p.puesto_sin_confirmar === true && (
                        <span className="text-xs text-muted-foreground">
                          {" "}
                          (puesto sin confirmar)
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3">
                      {[p.telefono, p.email].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="py-1.5 pr-3">
                      {p.perfil_url ? (
                        <a
                          href={p.perfil_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline"
                        >
                          Ver perfil
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-1.5">
                      <Src c={p} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-muted-foreground">
              {d.personas_descartadas && `${d.personas_descartadas}. `}
              Solo datos profesionales. Agrégalos como contactos desde la
              pestaña Contactos.
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No se encontraron personas clave con fuente pública.
            {d.personas_descartadas && ` ${d.personas_descartadas}.`}
          </p>
        )}
      </Section>
      <Section title="Registros públicos">
        <Rows
          rows={[
            ...(d.registros ?? []).map((x) => ({
              k: x.registro,
              v: x.valor,
              c: x,
            })),
            {
              k: "Lista 69-B del SAT",
              v: hits.length
                ? hits
                    .map(
                      (h) =>
                        `Aparece: ${h.nombre} (RFC ${h.rfc}) · ${h.situacion}`,
                    )
                    .join("; ")
                : "No aparece",
              c: { fuente: `Lista oficial del SAT, corte ${L?.corte ?? "—"}` },
            },
            ...(d.rfc ? [{ k: "RFC", v: d.rfc, c: {} }] : []),
          ]}
        />
      </Section>
      {!!d.competidores?.length && (
        <Section title="Competidores cercanos">
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {d.competidores.map((x, i) => (
              <li key={i}>
                <b>{x.nombre}</b>
                {x.ubicacion && ` · ${x.ubicacion}`} <Src c={x} />
              </li>
            ))}
          </ul>
          <span className="text-xs text-muted-foreground">
            Útil para decir «ya trabajamos con empresas como la suya en{" "}
            {f.pc.municipio}».
          </span>
        </Section>
      )}
      {!!d.riesgos?.length && (
        <Section title="Riesgos">
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {d.riesgos.map((x, i) => (
              <li key={i}>
                {x.texto} <Src c={x} />
              </li>
            ))}
          </ul>
        </Section>
      )}
      {!!r.sources?.length && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">
            Fuentes consultadas ({r.sources.length})
          </summary>
          <ul className="mt-1 space-y-0.5">
            {r.sources.map((s) => (
              <li key={s.url} className="truncate">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline"
                >
                  {s.title ? `${s.title} — ` : ""}
                  {s.url}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
